import classNames from "classnames";
import React, { useState } from "react";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaPopover from "../../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import "./popover-input.scss";

export type PopoverInputPosition = "top" | "right" | "bottom" | "left";
export type PopoverInputAlign = "start" | "center" | "end";
export type PopoverInputSize = "sm" | "md";

export interface PopoverInputProps {
  /** Serialized summary shown in the read-only input (e.g. "opacity 200ms ease"). */
  value: string | undefined;
  /** Shown when value is undefined/empty. Defaults to "None". */
  placeholder?: string;
  /** Popover content rendered when the input is clicked. */
  popoverContent: React.ReactNode;
  /** Optional popover title. */
  popoverTitle?: string | React.ReactNode;
  /** Popover side relative to the input. Defaults to "bottom". */
  popoverPosition?: PopoverInputPosition;
  /** Popover alignment along the side. Defaults to "center". */
  popoverAlign?: PopoverInputAlign;
  /** Extra class for the popover content surface. */
  popoverClassName?: string;
  /** Optional clear handler — shows a trash icon when value is set. */
  onClear?: () => void;
  /** Left icon (decorative, e.g. transition property icon). */
  leftIcon?: React.ReactNode;
  /** Disable interaction. */
  disabled?: boolean;
  /** Input size, defaults to "sm" (styling panel default). */
  size?: PopoverInputSize;
  /** Test id for the underlying input. */
  dataTestId?: string;
  /** Called when the popover opens. */
  onOpen?: () => void;
  /** Called when the popover closes. */
  onClose?: () => void;
  /** Extra class for the input wrapper. */
  className?: string;
}

/**
 * PopoverInput — reusable read-only input + KuikaPopover wrapper used by
 * Transitions (Faz 3) and Filters/BackdropFilters (Faz 4) multi-item lists.
 *
 * The input renders a summary string of the current value; clicking it opens
 * a controlled KuikaPopover whose content is supplied by the parent. A trash
 * icon appears on the right when `onClear` is provided AND `value` is set;
 * otherwise a chevron-down indicator is shown.
 */
const PopoverInput: React.FC<PopoverInputProps> = ({
  value,
  placeholder = "None",
  popoverContent,
  popoverTitle,
  popoverPosition = "bottom",
  popoverAlign = "center",
  popoverClassName,
  onClear,
  leftIcon,
  disabled,
  size = "sm",
  dataTestId,
  onOpen,
  onClose,
  className
}) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const handleVisibilityChange = (next: boolean): void => {
    setIsVisible(next);
    if (next) {
      onOpen?.();
    } else {
      onClose?.();
    }
  };

  const handleClear = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onClear?.();
  };

  const hasValue: boolean = value !== undefined && value !== null && value !== "";
  const showClear: boolean = hasValue && !!onClear && !disabled;

  const rightIcon: React.ReactNode = showClear ? (
    // Chevron shows at rest; the trash button is revealed on row hover/focus
    // (pure-CSS swap in popover-input.scss). Both stay mounted so no re-render.
    <span className="popover-input__right">
      <span className="popover-input__chevron" aria-hidden="true">
        <KuikaSvg name="chevron-down" width={12} height={12} />
      </span>
      <span
        role="button"
        tabIndex={0}
        aria-label="Clear"
        onClick={handleClear}
        onKeyDown={(e: React.KeyboardEvent): void => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClear?.();
          }
        }}
        className="popover-input__clear"
        data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
      >
        <KuikaSvg name="trash" width={14} height={14} />
      </span>
    </span>
  ) : (
    <KuikaSvg name="chevron-down" width={12} height={12} />
  );

  const wrapperClassName: string = classNames("popover-input", className, {
    "popover-input--disabled": disabled
  });

  return (
    <KuikaPopover
      visible={isVisible}
      onVisibleChange={handleVisibilityChange}
      content={popoverContent}
      title={popoverTitle}
      position={popoverPosition}
      align={popoverAlign}
      className={popoverClassName}
    >
      <div className={wrapperClassName}>
        <KuikaInput
          readOnly
          value={hasValue ? value : ""}
          placeholder={placeholder}
          leftIcon={leftIcon}
          rightIcon={rightIcon}
          disabled={disabled}
          size={size}
          data-testid={dataTestId}
          onClick={(): void => {
            if (!disabled) handleVisibilityChange(true);
          }}
        />
      </div>
    </KuikaPopover>
  );
};

export default PopoverInput;
